import { toast } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  cancelTrainingSession,
  createTrainingShareLink,
  getSessionRetrainAvailability,
  getTrainingSession,
  getTrainingSessionEvalSummary,
  getTrainingSessionProgress,
  isWorkspaceNotOwnedError,
  registerTrainingPolicy,
  retryTrainingSession,
  SESSION_DISPLAY_NAME_MAX_LENGTH,
  type SessionCheckpointView,
  type TrainingEvalSummaryView,
  type TrainingSessionDetailView,
  type TrainingSessionListView,
  type TrainingSessionProgressDetailView,
  type TrainingShareLinkTarget,
} from "@/api";
import { canUseEvaluation, useAppState } from "@/app";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";
import { isHttpClientError } from "@/http";
import { createLogger } from "@/logging";
import { downloadSessionCheckpoint } from "./sessionCheckpointDownload";
import { loadConfigDocument, type TrainingSessionConfigDocument } from "./sessionConfigDocument";
import {
  artifactPathTail,
  checkpointDisplayLabel,
  checkpointRowId,
} from "./sessionDetail/trainingSessionDetailFormat";
import {
  resolveCloneErrorMessage,
  resolveResumeErrorMessage,
  resolveRetrainDisabledReason,
  startSessionFrom,
} from "./sessionStartActions";
import {
  buildSessionResubmitPrefill,
  type SessionPrefillOptions,
} from "./submit/sessionResubmitPrefill";
import {
  derivedSessionDefaultName,
  formatSessionStatus,
  sessionDisplayName,
} from "./trainingSessionFormat";
import {
  isActiveSessionStatus,
  isResumableSessionStatus,
  isRetryableSessionStatus,
} from "@/modules/training/public";
import { openTrainingSubmitWindow } from "./trainingSubmitWindow";

const logger = createLogger("page/training/sessionDetail");

export type UseTrainingSessionDetailControllerOptions = {
  // 제출 직후 여는 창의 owner 강제 플래그. 제출 응답에는 선점자(registry_user)가 없어 행 비교를 할 수
  // 없다. 그러나 제출은 본인이 선점한 workspace 에만 가능하므로 owner 가 확정된다.
  owned?: boolean;
  // Config 전문은 별도 탭을 실제로 열었을 때만 MLflow 아티팩트를 조회한다.
  configEnabled?: boolean;
  // 창이 보이는 동안 진행 중 세션의 상태를 다시 조회해 terminal 전이를 전달한다.
  statusPollEnabled?: boolean;
};

// 상태 재조회 주기. 학습 곡선 폴링(useSessionMetrics)과 같은 주기로 맞춘다.
const statusPollMs = 15_000;

type RichState = LoadState<TrainingSessionDetailView>;
type ConfigState = LoadState<TrainingSessionConfigDocument | null>;
type ActionKind = "cancel" | "retry" | "resume" | "clone";

// cancel/retry 가 거절되는 두 경우를 구분해 안내한다. 버튼은 선점자와 admin 에게만 열지만, 화면을
// 연 뒤 상태나 선점이 바뀔 수 있다. 그래서 두 경우 모두 정상 경로에서 발생한다.
function resolveCommandErrorMessage(error: unknown): string {
  if (isWorkspaceNotOwnedError(error)) {
    return "이 Session을 다른 사용자가 선점하고 있어 취소나 재시도를 할 수 없습니다. 선점한 사용자에게 요청해 주세요.";
  }
  if (isHttpClientError(error) && error.status === 409) {
    return "이미 종료된 Session이라 이 동작을 적용할 수 없습니다. 최신 상태를 확인해 주세요.";
  }
  return resolveLoadErrorMessage(error);
}

// 세션 목록 행을 받아 상세 Window 의 데이터·권한·동작을 한곳에서 소유한다. 가시성은 workspace 선점
// 스코프다. 그래서 rich 상세, browse 보강(progress·eval summary), 체크포인트를 항상 받는다.
// cancel/retry 는 member+ 이면서 선점자(또는 admin)여야 하고, 정책 등록은 member+ 게이트다.
export function useTrainingSessionDetailController(
  session: TrainingSessionListView,
  options: UseTrainingSessionDetailControllerOptions = {},
) {
  const { owned = false, configEnabled = false, statusPollEnabled = false } = options;
  const sessionId = session.id;
  const { state: appState } = useAppState();

  const [richState, setRichState] = useState<RichState>({ status: "idle" });
  const [progressState, setProgressState] = useState<LoadState<TrainingSessionProgressDetailView>>({
    status: "idle",
  });
  const [evalState, setEvalState] = useState<LoadState<TrainingEvalSummaryView>>({
    status: "idle",
  });
  // duration 필드(node_lifetime·eta_seconds)를 로컬에서 줄이는 기준 시각. 응답이 "남은 초"라서 받은
  // 순간부터 오래된 값이 된다. 그래서 표시 쪽이 (현재 - 수신 시각)만큼 빼서 쓴다.
  const [richReceivedAtMs, setRichReceivedAtMs] = useState<number | null>(null);
  const [progressReceivedAtMs, setProgressReceivedAtMs] = useState<number | null>(null);
  const [configState, setConfigState] = useState<ConfigState>({ status: "idle" });
  // StrictMode 재실행과 탭 재방문에서 같은 run/path 후보를 중복 요청하지 않는다.
  const configRequestsRef = useRef(
    new Map<string, Promise<TrainingSessionConfigDocument | null>>(),
  );
  const [actionPending, setActionPending] = useState<ActionKind | null>(null);
  // 이어서 학습 확인 모달. 열려 있으면 새 세션 이름을 받는 중이다. 실패 문구는 모달 안에서 표시한다
  // (토스트로 표시하면 모달을 닫아야 읽을 수 있고, 이름을 다시 입력해야 한다).
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resumeErrorMessage, setResumeErrorMessage] = useState<string | null>(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneErrorMessage, setCloneErrorMessage] = useState<string | null>(null);
  const [downloadingEpoch, setDownloadingEpoch] = useState<string | null>(null);
  // 등록 진행 중인 행의 키(checkpointRowId). 태그 없는 항목은 epoch 이 없어 태그를 키로 쓸 수 없다.
  const [registeringRowId, setRegisteringRowId] = useState<string | null>(null);
  // 재학습 프로브. 이 세션에 재개 가능한 raw 체크포인트가 있는지 확인한다(ZERG-266 축2). 상세를 열 때
  // 한 번만 묻고, 액션의 활성 여부와 사유를 여기서 정한다. 실패는 무시한다. 상류 장애 때문에 화면 전체를
  // 쓸 수 없게 할 이유가 없고, 제출할 때 BE 가 다시 판정한다.
  const [retrainState, setRetrainState] = useState<LoadState<boolean>>({ status: "idle" });

  // 스코프 안 세션은 누구나 rich 상세를 받는다(계약상 shallow 상태가 없다).
  const loadRichRequest = useCallback(async () => {
    // 다시 조회하는 동안(상태 폴링·refresh) 성공 데이터를 유지한다. rich 가 null 로 바뀌면 제목과
    // 상태 칩이 깜빡이고, effectiveStatus 가 잠시 목록 행 값으로 돌아간다.
    setRichState((prev) => (prev.status === "success" ? prev : { status: "loading" }));
    try {
      const detail = await getTrainingSession(sessionId);
      setRichState({ status: "success", data: detail });
      setRichReceivedAtMs(Date.now());
    } catch (error) {
      // 404 = 창이 열린 동안 workspace 선점이 바뀌어 스코프 밖이 된 race 다.
      const message =
        isHttpClientError(error) && error.status === 404
          ? "Session을 찾을 수 없습니다. 선점이 변경되었을 수 있습니다."
          : resolveLoadErrorMessage(error);
      setRichState({ status: "error", message });
    }
  }, [sessionId]);

  // Checkpoint 탭으로 바로 열면 상세 초기 조회와 탭 갱신이 겹친다.
  // 같은 Session 의 진행 중 요청을 공유해 상류 fan-out 을 중복 실행하지 않는다.
  const richRequestRef = useRef<{ sessionId: string; pending: Promise<void> } | null>(null);
  const loadRich = useCallback(() => {
    if (richRequestRef.current?.sessionId === sessionId) return richRequestRef.current.pending;
    const pending = loadRichRequest().finally(() => {
      if (richRequestRef.current?.pending === pending) richRequestRef.current = null;
    });
    richRequestRef.current = { sessionId, pending };
    return pending;
  }, [sessionId, loadRichRequest]);

  // 세션의 진행 상태와 eval summary 를 병렬로 받는다. eval summary 는 evaluation 권한 게이트(403)
  // 안에 있다. 그래서 권한이 없으면 호출하지 않고 빈 집계로 둔다. 세션 상세의 나머지 화면은 권한과
  // 무관하게 표시된다.
  const canUseEvaluationSummary = canUseEvaluation(appState.currentUser?.permissions ?? null);
  const loadBrowseExtras = useCallback(async () => {
    setProgressState({ status: "loading" });
    setEvalState({ status: "loading" });
    await Promise.all([
      getTrainingSessionProgress(sessionId).then(
        (data) => {
          setProgressState({ status: "success", data });
          setProgressReceivedAtMs(Date.now());
        },
        (error: unknown) =>
          setProgressState({ status: "error", message: resolveLoadErrorMessage(error) }),
      ),
      canUseEvaluationSummary
        ? getTrainingSessionEvalSummary(sessionId).then(
            (data) => setEvalState({ status: "success", data }),
            (error: unknown) =>
              setEvalState({ status: "error", message: resolveLoadErrorMessage(error) }),
          )
        : Promise.resolve(
            setEvalState({
              status: "success",
              data: {
                session_id: sessionId,
                total: 0,
                by_status: {},
                scenarios: [],
                eval_runs: [],
              },
            }),
          ),
    ]);
  }, [sessionId, canUseEvaluationSummary]);

  useEffect(() => {
    void loadRich();
    void loadBrowseExtras();
  }, [loadRich, loadBrowseExtras]);

  const rich = richState.status === "success" ? richState.data : null;
  // run id 는 rich 상세의 mlflow_run_id 가 유일한 소스다. progress 는 trainer push 경로에서
  // run_id=null 로 응답하고, mlflow_run_url 은 /runs/ 가 없는 호스트 힌트다.
  const configRunId = rich?.mlflow_run_id ?? null;

  useEffect(() => {
    if (!configEnabled) {
      return;
    }
    if (richState.status === "idle" || richState.status === "loading") {
      setConfigState({ status: "loading" });
      return;
    }
    if (configRunId === null) {
      // run 이 아직 없다(대기 중·run 생성 전 실패). Config 전문 미제공으로 표시한다.
      setConfigState({ status: "success", data: null });
      return;
    }

    let cancelled = false;
    setConfigState({ status: "loading" });
    let request = configRequestsRef.current.get(configRunId);
    if (!request) {
      request = loadConfigDocument(configRunId);
      configRequestsRef.current.set(configRunId, request);
    }
    void request
      .then((document) => {
        if (document === null) {
          configRequestsRef.current.delete(configRunId);
        }
        if (!cancelled) {
          setConfigState({ status: "success", data: document });
        }
      })
      .catch((error: unknown) => {
        configRequestsRef.current.delete(configRunId);
        if (!cancelled) {
          logger.error("config artifact lookup failed", error);
          setConfigState({
            status: "error",
            message: "Config 전문을 불러오지 못했습니다.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [configEnabled, configRunId, richState.status]);

  // 소유(선점) 판별. 목록 행의 registry_user(= workspace 선점자)와 내 member uuid 를 비교한다.
  // admin 은 모든 세션에 명령을 실행할 수 있다(BE 게이트와 같다).
  const myMemberUuid = appState.currentUser?.memberUuid ?? null;
  const isOwner = owned || (myMemberUuid !== null && session.registry_user?.uuid === myMemberUuid);
  const isAdmin = appState.currentUser?.role === "admin";
  const canCommand = isOwner || isAdmin;
  // 체크포인트 공유 링크 발급 요청. 복사·실패 문구·클립보드 폴백은 패널의 공용 발급 훅이 소유한다.
  const shareCheckpoint = useCallback(
    (target: TrainingShareLinkTarget) => createTrainingShareLink(target),
    [],
  );
  // 액션 게이트는 rich status 가 있으면 그 값으로, 없으면 목록 행 status 로 판단한다.
  const effectiveStatus = rich?.status ?? session.status;
  // ETA API 의 phase 는 실행 단계가 아니다. 목록과 같은 상세 progress 만 쓴다. 그 필드를 제공하지
  // 않는 서버이거나 상세를 로딩하는 중이면 목록 스냅샷을 표시한다.
  const statusLabel = formatSessionStatus({
    status: effectiveStatus,
    progress: rich?.progress === undefined ? session.progress : rich.progress,
  });

  // 진행 중 세션의 상태를 주기적으로 다시 조회해 terminal 전이를 창에 전달한다. 헤더 칩, 곡선 폴링
  // 정지(live), 실패 진단 카드(isFailure)가 이 전이에 의존한다. richState 갱신이 다음 타이머를 다시
  // 등록하는 체인 구조다. 그래서 진행 중인 재조회와 겹치지 않고, terminal 이 되면 타이머 등록을
  // 멈춘다.
  useEffect(() => {
    if (richState.status === "loading") {
      // 첫 로드 중이다. 완료되면 richState 를 갱신하면서 타이머를 다시 등록한다.
      return;
    }
    if (!statusPollEnabled || !isActiveSessionStatus(effectiveStatus)) {
      return;
    }
    const timer = setTimeout(() => {
      void loadRich();
    }, statusPollMs);
    return () => clearTimeout(timer);
  }, [richState, statusPollEnabled, effectiveStatus, loadRich]);
  const canCancel = canCommand && isActiveSessionStatus(effectiveStatus);
  const canRetry = canCommand && isRetryableSessionStatus(effectiveStatus);
  const canResume = canCommand && isResumableSessionStatus(effectiveStatus);
  // 복제는 상태 제약이 없다. 재현 방식이라 실행 중이든 종료 후든 원본 recipe 그대로 다시 제출할 수
  // 있다(BE 와 상류 모두 스코프 + 선점자/admin 게이트뿐이다).
  const canClone = canCommand;
  // 설정 고쳐 다시 제출(ZERG-257). 세션 명령이 아니라 신규 제출이므로 선점 게이트를 거치지 않는다
  // (워크스페이스와 실험은 위저드에서 새로 고른다). rich 가 있어야 프리필 데이터가 있다.
  const canResubmit = rich !== null;
  // 재학습도 같은 신규 제출 경로다. 게이트는 재제출과 같다. 실제로 시작할 수 있는지는 체크포인트
  // 유무(프로브)로 따로 판정한다.
  const canRetrain = canResubmit && retrainState.status === "success" && retrainState.data;
  const retrainDisabledReason = resolveRetrainDisabledReason(retrainState);

  // 프로브는 상세를 여는 시점에 한 번 실행한다. 상태 폴링에 맞춰 반복할 이유가 없다(체크포인트는
  // 학습이 진행되면서 생길 뿐 사라지지 않는다).
  useEffect(() => {
    let cancelled = false;
    setRetrainState({ status: "loading" });
    void getSessionRetrainAvailability(sessionId).then(
      (available) => {
        if (!cancelled) {
          setRetrainState({ status: "success", data: available });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          logger.error("retrain availability probe failed", error);
          setRetrainState({ status: "error", message: resolveLoadErrorMessage(error) });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
  // dock/헤더 제목. rich 이름이 있으면 그 이름을, 없으면 목록 행 폴백(id 단축)을 쓴다.
  const title = rich?.display_name ?? sessionDisplayName(session);

  const runAction = useCallback(
    async (kind: "cancel" | "retry") => {
      if ((kind === "cancel" && !canCancel) || (kind === "retry" && !canRetry)) {
        return;
      }
      setActionPending(kind);
      try {
        if (kind === "cancel") {
          await cancelTrainingSession(sessionId);
          toast.info("Session 취소를 요청했습니다.");
        } else {
          await retryTrainingSession(sessionId);
          toast.info("Session 재시도를 요청했습니다.");
        }
        await Promise.all([loadRich(), loadBrowseExtras()]);
      } catch (error) {
        toast.error(resolveCommandErrorMessage(error));
        await loadRich();
      } finally {
        setActionPending(null);
      }
    },
    [canCancel, canRetry, sessionId, loadRich, loadBrowseExtras],
  );

  const cancel = useCallback(() => {
    void runAction("cancel");
  }, [runAction]);

  const retry = useCallback(() => {
    void runAction("retry");
  }, [runAction]);

  const openResumeDialog = useCallback(() => {
    setResumeErrorMessage(null);
    setResumeDialogOpen(true);
  }, []);

  const closeResumeDialog = useCallback(() => {
    setResumeDialogOpen(false);
    setResumeErrorMessage(null);
  }, []);

  // 이어서 학습. 부모를 이어받는 **새 세션**이 생긴다(같은 세션을 제자리에서 다시 실행하는 retry 와 다르다).
  // 상류가 이름을 만들지 않으므로 만든 직후 rename 으로 사용자가 고른 이름을 적용한다.
  const confirmResume = useCallback(
    async (displayName: string) => {
      if (!canResume || actionPending !== null) {
        return;
      }
      setActionPending("resume");
      setResumeErrorMessage(null);
      try {
        await startSessionFrom("resume", { sessionId, displayName });
        setResumeDialogOpen(false);
      } catch (error) {
        // 모달을 연 채로 사유를 표시한다. 사용자가 이름을 다시 입력하지 않게 한다.
        setResumeErrorMessage(resolveResumeErrorMessage(error));
      } finally {
        setActionPending(null);
      }
    },
    [canResume, actionPending, sessionId],
  );

  const resume = useCallback(
    (displayName: string) => {
      void confirmResume(displayName);
    },
    [confirmResume],
  );

  const openCloneDialog = useCallback(() => {
    setCloneErrorMessage(null);
    setCloneDialogOpen(true);
  }, []);

  const closeCloneDialog = useCallback(() => {
    setCloneDialogOpen(false);
    setCloneErrorMessage(null);
  }, []);

  // 복제. 원본 recipe 를 그대로 다시 제출하는 **새 세션**이 생긴다(재현 방식, 새 lineage).
  // resume 과 같은 이유로 만든 직후 rename 으로 사용자가 고른 이름을 적용한다.
  const confirmClone = useCallback(
    async (displayName: string) => {
      if (!canClone || actionPending !== null) {
        return;
      }
      setActionPending("clone");
      setCloneErrorMessage(null);
      try {
        await startSessionFrom("clone", { sessionId, displayName });
        setCloneDialogOpen(false);
      } catch (error) {
        // 모달을 연 채로 사유를 표시한다. 사용자가 이름을 다시 입력하지 않게 한다.
        setCloneErrorMessage(resolveCloneErrorMessage(error));
      } finally {
        setActionPending(null);
      }
    },
    [canClone, actionPending, sessionId],
  );

  const clone = useCallback(
    (displayName: string) => {
      void confirmClone(displayName);
    },
    [confirmClone],
  );

  // 이 세션의 제출 스냅샷을 시드로 넣어 위저드를 연다. 진입점은 둘(재제출·재학습)이고 시작점만
  // 다르다. 그래서 여는 절차는 하나로 둔다. 이어받지 못한 항목은 여는 시점에 한 번 알린다.
  const openPrefilledSubmit = useCallback(
    (options: SessionPrefillOptions) => {
      if (rich === null) {
        return;
      }
      const prefill = buildSessionResubmitPrefill(rich, title, options);
      openTrainingSubmitWindow({ prefill });
      if (prefill.omissions.length > 0) {
        toast.info(prefill.omissions.join(" "));
      }
    },
    [rich, title],
  );

  // 실패 진단 카드의 "설정 고쳐 다시 제출". 수정 없는 재실행은 재시도가 맡고, 여기는 수정 후 신규
  // 제출 경로다(두 경우는 사용자가 버튼으로 고른다). 시작점은 원본과 같은 scratch 다.
  const openResubmit = useCallback(() => {
    if (!canResubmit) {
      return;
    }
    openPrefilledSubmit({});
  }, [canResubmit, openPrefilledSubmit]);

  // 헤더의 "재학습". 같은 스냅샷을 쓰되 시작점이 이 세션의 마지막 체크포인트다. 상류가 계보를
  // 기록하므로 재제출과 달리 이름 파생만으로 이어지는 관계가 아니다.
  const openRetrain = useCallback(() => {
    if (!canRetrain) {
      return;
    }
    openPrefilledSubmit({ retrainSource: { id: sessionId, title } });
  }, [canRetrain, openPrefilledSubmit, sessionId, title]);

  // 새 세션 이름의 기본값. 부모 이름에서 파생한다(연구자들의 `-x2`/`-x3` 관례). resume 과 clone 이
  // 같은 관례를 공유한다.
  const newSessionDefaultName = derivedSessionDefaultName(title, SESSION_DISPLAY_NAME_MAX_LENGTH);

  // 다운로드는 스코프 전체에 허용하므로 owner 게이트가 없다. 발급·파일명·실패 문구는
  // sessionCheckpointDownload 가 소유하고, 여기서는 분석 탭과 Checkpoint 탭의 pending 표시를 관리한다.
  const downloadCheckpoint = useCallback(
    async (epoch: string) => {
      setDownloadingEpoch(epoch);
      try {
        await downloadSessionCheckpoint(sessionId, epoch);
      } finally {
        setDownloadingEpoch(null);
      }
    },
    [sessionId],
  );

  // 체크포인트를 정책 카탈로그에 등록한다. artifact_uri 가 멱등 키라서 다시 등록하면 기존 정책을
  // 그대로 반환한다. source_session_id 는 정식 training 세션 id(스코프 게이트 키)를 쓴다.
  const registerCheckpoint = useCallback(
    async (checkpoint: SessionCheckpointView) => {
      if (rich === null) {
        return;
      }
      if (checkpoint.artifact_uri === null) {
        // 이 체크포인트에 artifact 위치가 없는 경우(MLflow run 미기록 등). 등록할 수 없다.
        toast.error("이 Checkpoint에는 Artifact 위치 정보가 없어 등록할 수 없습니다.");
        return;
      }
      setRegisteringRowId(checkpointRowId(checkpoint));
      // 이름 접미사는 표의 라벨과 같은 규칙이다. 태그 없는 항목은 고정 문구 대신 artifact 경로
      // 끝부분으로 정책끼리 구분한다.
      const suffix =
        checkpoint.epoch !== null
          ? checkpointDisplayLabel(checkpoint.epoch)
          : artifactPathTail(checkpoint.artifact_uri);
      try {
        await registerTrainingPolicy({
          name: `${title} · ${suffix}`,
          artifact_uri: checkpoint.artifact_uri,
          source_session_id: rich.training_session_id,
          experiment_id: session.experiment_id ?? undefined,
        });
        toast.success("Policy 로 등록했습니다. Evaluation 페이지에서 확인해 주세요.");
      } catch (error) {
        const message =
          isHttpClientError(error) && error.status === 409
            ? "이미 Policy 로 등록된 Checkpoint입니다. Evaluation 페이지에서 확인해 주세요."
            : resolveLoadErrorMessage(error);
        toast.error(message);
      } finally {
        setRegisteringRowId(null);
      }
    },
    [rich, session.experiment_id, title],
  );

  const refresh = useCallback(() => {
    void loadRich();
    void loadBrowseExtras();
  }, [loadRich, loadBrowseExtras]);

  return {
    session,
    title,
    richState,
    rich,
    richReceivedAtMs,
    progressState,
    progressReceivedAtMs,
    evalState,
    configState,
    isOwner,
    shareCheckpoint,
    effectiveStatus,
    statusLabel,
    canCancel,
    canRetry,
    canResume,
    canClone,
    actionPending,
    downloadingEpoch,
    registeringRowId,
    cancel,
    retry,
    resumeDialogOpen,
    resumeErrorMessage,
    newSessionDefaultName,
    openResumeDialog,
    closeResumeDialog,
    resume,
    cloneDialogOpen,
    cloneErrorMessage,
    openCloneDialog,
    closeCloneDialog,
    clone,
    canResubmit,
    openResubmit,
    canRetrain,
    // 버튼을 누를 수 없는 이유. 프로브 완료 전, 체크포인트 없음, 확인 실패를 구분한다.
    retrainDisabledReason,
    openRetrain,
    downloadCheckpoint,
    registerCheckpoint,
    refresh,
    refreshCheckpoints: loadRich,
  };
}

export type TrainingSessionDetailController = ReturnType<typeof useTrainingSessionDetailController>;
